import React, { memo, useCallback, useMemo, useState } from 'react'
import ApolloErrorMessages from 'transfix-ui/components/ApolloErrorMessages'
import InlineMessage, { INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import {
  ConfirmationModal,
  IConfirmationModalProps,
} from 'transfix-ui/components/Modals/ConfirmationModal'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { Typography, makeStyles } from 'transfix-ui/core'
import { IError } from 'types/graphqlTypes'
import CoverModal from 'apps/marketplace/components/CoverModal'
import { useMPAnalytics } from 'apps/marketplace/hooks/useMPAnalytics'
import formatCurrency from 'utils/formatCurrency'
import { useModalState } from 'utils/hooks'
import { TEST_IDS } from './constants'
import { useAcceptInboundOptionMutation } from './graphql/AcceptInboundOption'
import { IAcceptInboundCarrierOption } from './types'
import { hasCarrierComplianceError } from './utils'

export interface IAcceptInboundOptionConfirmationModalProps
  extends Pick<IConfirmationModalProps, 'onClose'> {
  option: IAcceptInboundCarrierOption
  onSuccess: () => void
}

const useAcceptInboundOptionConfirmationModalStyles = makeStyles(theme => ({
  bold: {
    fontWeight: 'bold',
  },
  nonCompliantError: {
    marginBottom: theme.spacing(2),
    '& button': {
      padding: 0,
    },
  },
}))

const AcceptInboundOptionConfirmationModal = memo<IAcceptInboundOptionConfirmationModalProps>(
  ({ onClose, onSuccess, option }) => {
    const { trackEvent } = useMPAnalytics()
    const classes = useAcceptInboundOptionConfirmationModalStyles()

    const { createToggleHandler, modalState } = useModalState({
      coverageModal: false,
    })
    const { coverageModal: isCoverageModalOpen } = modalState || {}
    const toggleCoverageModalHandler = createToggleHandler('coverageModal')

    const [isCarrierNonCompliant, setIsCarrierNonCompliant] = useState<boolean>(false)
    const [mutationErrors, setMutationErrors] = useState<IError[]>([])

    const { amount: rate, carrierApplication, id: optionId, shipmentId } = option || {}
    const { name: carrierApplicationName } = carrierApplication || {}

    const { openToast } = useGlobalToast()
    const [acceptInboundOption, { error, loading }] = useAcceptInboundOptionMutation()

    const acceptDigitalBid = useCallback(async () => {
      setMutationErrors([])
      const { data } = await acceptInboundOption({
        variables: {
          input: {
            id: optionId,
          },
        },
      })
      const { acceptInboundBid } = data || {}
      const { bid, errors = [] } = acceptInboundBid || {}
      const { id: bidId } = bid || {}

      if (errors.length) {
        const hasComplianceError = hasCarrierComplianceError(errors)
        setIsCarrierNonCompliant(hasComplianceError)
        trackEvent(
          'Carrier Options',
          hasComplianceError
            ? 'ERROR_ACCEPTING_NON_COMPLIANT_INBOUND_OPTION'
            : 'ERROR_ACCEPTING_INBOUND_OPTION',
          {
            shipmentId,
            carrierOptionId: optionId,
            errors,
          }
        )
        setMutationErrors(errors)
        return
      }

      if (bidId) {
        trackEvent('Carrier Options', 'ACCEPTED_INBOUND_OPTION', {
          shipmentId,
          carrierOptionId: bidId,
        })
        openToast({
          toastMessage: 'Option Accepted',
          toastType: 'default',
          toastCta: {
            label: `Shipment #${shipmentId}`,
            url: `/shipment-details/${shipmentId}`,
            target: '_blank',
          },
        })
        onSuccess()
      }
    }, [acceptInboundOption, onSuccess, openToast, optionId, shipmentId, trackEvent])

    const coverSuccessHandler = useCallback(() => {
      trackEvent('Carrier Options', 'COVERED_SHIPMENT_WITH_NON_COMPLIANT_INBOUND_OPTION', {
        shipmentId,
        carrierOptionId: optionId,
      })
    }, [optionId, shipmentId, trackEvent])

    const modalActions: IConfirmationModalProps['actions'] = useMemo(
      () => [
        {
          label: 'No, Cancel',
          type: 'dismiss',
          disabled: loading,
          action: onClose,
        },
        {
          label: `Yes, Accept & Send`,
          type: 'confirm',
          dataTest: TEST_IDS.CONFIRMATION_MODAL_SUBMIT,
          disabled: loading,
          action: acceptDigitalBid,
        },
      ],
      [loading, onClose, acceptDigitalBid]
    )

    return (
      <>
        <ConfirmationModal
          title='Accept this option?'
          dataTest={TEST_IDS.CONFIRMATION_MODAL}
          actions={modalActions}
          onClose={onClose}
          open>
          {!isCarrierNonCompliant && (
            <ApolloErrorMessages mutationErrors={mutationErrors} apolloErrors={error} />
          )}
          {isCarrierNonCompliant && (
            <InlineMessage
              message='Carrier is missing compliance requirements. Would you like to cover the shipment with this option instead?'
              type={INLINE_MESSAGE_VARIANTS.ALERT}
              cta={{ label: 'Cover with Option', action: toggleCoverageModalHandler }}
              className={classes.nonCompliantError}
              dataTest={TEST_IDS.NON_COMPLIANT_ERROR}
              fullWidth
            />
          )}
          <Typography variant='body1'>
            Are you sure you want to accept{' '}
            <span className={classes.bold}>{carrierApplicationName}</span> bid of{' '}
            <span className={classes.bold}>{formatCurrency(rate)}</span> and send the ratecon?
          </Typography>
        </ConfirmationModal>
        {isCoverageModalOpen && (
          <CoverModal
            shipmentId={shipmentId}
            carrierOption={option}
            onClose={onClose}
            onSuccess={coverSuccessHandler}
          />
        )}
      </>
    )
  }
)

export default AcceptInboundOptionConfirmationModal
